import type { HttpClient, RequestOptions } from '../client.js';
import {
  type YieldsData,
  type PoolFilterParams,
  type BestYieldsData,
  type GetYieldsBalanceParams,
  type GetYieldsPriceParams,
  type GetYieldsPendleImpliedApyParams,
  type PoolPriceData,
  type PoolBalanceData,
  type MorphoVaultsData,
  type MorphoVaultDataList,
  poolFilterParamsSchema,
  getYieldsBestParamsSchema,
  getYieldsBalanceParamsSchema,
  getYieldsPriceParamsSchema,
  getYieldsPendleImpliedApyParamsSchema,
  getMorphoVaultsParamsSchema,
  getMorphoVaultDataParamsSchema,
} from '@spectrum-nodes/api-spec/models';
import type { StakingFilter, BestYieldsFilter } from '../types/index.js';
import { parseParams } from '../internals/validation.js';

/** DeFi yield data — lending, vaults, staking, best yields */
export class YieldsNamespace {
  constructor(private readonly client: HttpClient) {}

  /**
   * Get lending protocol yields (APY, TVL).
   * Requires chain, protocol, and pool to be specified.
   *
   * Supported protocols: aave, compound, morpho, hyperlend
   */
  async getLending(filter?: PoolFilterParams, options?: RequestOptions): Promise<YieldsData> {
    const validated = parseParams(poolFilterParamsSchema, {
      chain: filter?.chain,
      protocol: filter?.protocol,
      pool: filter?.pool,
      blockHeight: filter?.blockHeight,
    });
    return this.client.jsonRpc<YieldsData>('getYieldsLending', validated, options);
  }

  /**
   * Get vault protocol yields (APY, TVL).
   * Requires chain, protocol, and pool to be specified.
   *
   * Supported protocols: beefy, yearn, pendle, hyperliquid
   */
  async getVaults(filter?: PoolFilterParams, options?: RequestOptions): Promise<YieldsData> {
    const validated = parseParams(poolFilterParamsSchema, {
      chain: filter?.chain,
      protocol: filter?.protocol,
      pool: filter?.pool,
      blockHeight: filter?.blockHeight,
    });
    return this.client.jsonRpc<YieldsData>('getYieldsVaults', validated, options);
  }

  /**
   * Get staking yields across protocols and chains.
   *
   * Calls the `getYieldsBest` JSON-RPC method with `type=staking` for
   * server-side filtering. Chain filtering is applied client-side after fetch.
   *
   * Sources: Lido, Rocket Pool, Jito, Marinade, BlazeStake
   */
  async getStaking(filter?: StakingFilter, options?: RequestOptions): Promise<BestYieldsData> {
    const validated = parseParams(getYieldsBestParamsSchema, {
      token: filter?.token,
      type: 'staking',
      limit: filter?.limit ?? 100,
      blockHeight: filter?.blockHeight,
    });
    const result = await this.client.jsonRpc<BestYieldsData>('getYieldsBest', validated, options);

    if (filter?.chain && Array.isArray(result.results)) {
      return {
        ...result,
        results: result.results.filter((entry) => entry.chain === filter.chain),
      };
    }

    return result;
  }

  /**
   * Get the best yields across lending, vaults, and staking.
   * Returns a ranked list sorted by total APY.
   */
  async getBest(filter?: BestYieldsFilter, options?: RequestOptions): Promise<BestYieldsData> {
    const validated = parseParams(getYieldsBestParamsSchema, {
      token: filter?.token,
      minTvl: filter?.minTvl,
      limit: filter?.limit,
      type: filter?.type === 'all' ? undefined : filter?.type,
      blockHeight: filter?.blockHeight,
    });
    return this.client.jsonRpc<BestYieldsData>('getYieldsBest', validated, options);
  }

  /**
   * Get user balance in a specific yield pool.
   * Requires chain, protocol, pool, and address.
   */
  async getBalance(
    filter: GetYieldsBalanceParams,
    options?: RequestOptions,
  ): Promise<PoolBalanceData> {
    const validated = parseParams(getYieldsBalanceParamsSchema, {
      chain: filter.chain,
      protocol: filter.protocol,
      pool: filter.pool,
      address: filter.address,
      blockHeight: filter.blockHeight,
    });
    return this.client.jsonRpc<PoolBalanceData>('getYieldsBalance', validated, options);
  }

  /**
   * Get pool price for a yield protocol.
   * Supported protocols: aave, morpho, pendle, midas.
   */
  async getPrice(filter: GetYieldsPriceParams, options?: RequestOptions): Promise<PoolPriceData> {
    const validated = parseParams(getYieldsPriceParamsSchema, {
      chain: filter.chain,
      protocol: filter.protocol,
      pool: filter.pool,
      blockHeight: filter.blockHeight,
    });
    return this.client.jsonRpc<PoolPriceData>('getYieldsPrice', validated, options);
  }

  /**
   * Get Pendle implied APY for a user's position.
   * Requires chain, pool, and address.
   */
  async getPendleImpliedApy(
    filter: GetYieldsPendleImpliedApyParams,
    options?: RequestOptions,
  ): Promise<YieldsData> {
    const validated = parseParams(getYieldsPendleImpliedApyParamsSchema, {
      chain: filter.chain,
      pool: filter.pool,
      address: filter.address,
      blockHeight: filter.blockHeight,
    });
    return this.client.jsonRpc<YieldsData>('getYieldsPendleImpliedApy', validated, options);
  }

  /**
   * List all available Morpho vaults on a chain.
   */
  async getMorphoVaults(chain: string, options?: RequestOptions): Promise<MorphoVaultsData> {
    const validated = parseParams(getMorphoVaultsParamsSchema, { chain });
    return this.client.jsonRpc<MorphoVaultsData>('getMorphoVaults', validated, options);
  }

  /**
   * Get detailed data for specific Morpho vaults (APY, TVL, fees, rewards).
   * Maximum 50 vaults per request.
   */
  async getMorphoVaultData(
    chain: string,
    vaults: string[],
    options?: RequestOptions,
  ): Promise<MorphoVaultDataList> {
    const validated = parseParams(getMorphoVaultDataParamsSchema, { chain, vaults });
    return this.client.jsonRpc<MorphoVaultDataList>('getMorphoVaultData', validated, options);
  }
}
